import axios from 'axios'
import { useCookies } from 'vue3-cookies'
import router from '@/router'
import { useAuthStore } from '@/stores/authStore'

const { cookies } = useCookies()

const TOKEN_ACCESS_NAME = 'app-gis_access_token'
const TOKEN_REFRESH_NAME = 'app-gis_refresh_token'

const BASE_URLS = {
  development: 'http://127.0.0.1:8000/api/',
  production: `${location.protocol}//${location.host}/api/`,
}

export function getBaseUrl() {
  const host = document.location.host

  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return BASE_URLS.development
  }

  return BASE_URLS.production
}

export const mediaRoot = getBaseUrl().slice(0, -1)

export const BASE_URL = axios.create({
  baseURL: getBaseUrl(),
  timeout: 10000,
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: any) => void
}> = []

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token!)
    }
  })
  failedQueue = []
}

BASE_URL.interceptors.request.use((config) => {
  const accessToken = cookies.get(TOKEN_ACCESS_NAME)
  const url = config.url || ''

  const isAuthRequest =
    url.includes('/auth/token-admin/') ||
    url.includes('/auth/token/') ||
    url.includes('/auth/refresh/') ||
    url.includes('/auth/logout/')

  if (accessToken && !isAuthRequest) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

BASE_URL.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config
    const status = error?.response?.status

    if (status === 401 && !originalRequest._retry) {
      const refreshToken = cookies.get(TOKEN_REFRESH_NAME)

      if (!refreshToken) {
        const authStore = useAuthStore()
        authStore.logoutUser()
        if (router.currentRoute.value.name !== 'login') {
          await router.push({ name: 'login' })
        }
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return BASE_URL(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await axios.post(getBaseUrl() + 'auth/token/refresh/', {
          refresh_token: refreshToken,
        }, { timeout: 10000 })

        const authStore = useAuthStore()

        if (response.data.access_token) {
          authStore.updateToken(response.data.access_token, 60 * 60 * 24 * 30)
        }
        if (response.data.refresh_token) {
          authStore.updateRefreshToken(response.data.refresh_token, 60 * 60 * 24 * 30)
        }

        processQueue(null, response.data.access_token)

        originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`
        return BASE_URL(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)

        const authStore = useAuthStore()
        authStore.logoutUser()
        if (router.currentRoute.value.name !== 'login') {
          await router.push({ name: 'login' })
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (status === 400) {
      console.error('Ошибка 400:', error.response?.data)
    }

    if (status === 403) {
      console.error('Ошибка 403:', error.response?.data)
    }

    if (status === 500) {
      console.error('Ошибка 500:', error.response?.data)
    }

    return Promise.reject(error)
  }
)

export function getAuthHeader() {
  const accessToken = cookies.get(TOKEN_ACCESS_NAME)
  return accessToken ? `Bearer ${accessToken}` : ''
}

export const AUTH_COOKIES = {
  access: TOKEN_ACCESS_NAME,
  refresh: TOKEN_REFRESH_NAME,
}